/**
 * @ngdoc Directive
 * @name vrmGraphCustomWidget
 * @example
 *
 * <vrmGraphCustomWidget installation="installation" />
 *
 */
import angular from 'angular';
import { mixinZoomState } from '@/angular/modules/vrm-components/graph-widget/zoomstate';
import { cloneDeep, sortBy } from 'lodash';
import { generateConfigBasedOfSeries } from './graph-configs';
import { RIGHT_MARGIN_AXES, LEFT_MARGIN_AXES } from "@/enums/custom-widget-axis-customisation";
import { MONETARY_ATTRIBUTES_CODES } from '@/vue/constants/attribute-types';
import { handleLocalStorageBool, handleInitialLocalStorageBool } from '@/angular/utils/handleLocalStorage'

angular.module('vrm.components')
    .directive('vrmGraphCustomWidget', vrmGraphCustomWidget);

const FILTER_NULL_POINTS_ATTRIBUTE_IDS = [
    1172, 1173,                             // Solar Irradiance
    1296,                                   // DESS scheduled SOC
    1280,                                   // Battery costs for energy in and out
    1281, 1282,                             // Grid buy / sell price
    843, 844, 846, 858, 875, 891, 907, 908  // Total energy
]

/* @ngInject */
function vrmGraphCustomWidget() {
    return {
        restrict: 'E',
        template: require('@/angular/modules/vrm-components/graph-widget/graph-custom-widget.template.html'),
        controller,
        scope: {
            widgetId: '=',
            installation: '=',
            data: '=',
            series: '=',
            title: '=',
            type: '=',
            tooltip: '=',
            loading: '=',
            zoomstate: '=',
            iconClass: '=',
            range: '=?',
            isMobile: '=',
            isAdvancedView: '=',
            hasTouchEnabled: '=',
            yAxisProperties: '=',
            size: '=',
            itemState: '=?',
            persistentMode: '=',
            canBeUpdated: '=',
            isAiAssistant: '=?',
        }
    };

    /* @ngInject */
    function controller($scope, $q, GraphService, localStorageService, AdvancedGraphWidgetConfigs, locale, WhitelabelService, moment) {
        let loaded = false;
        let lineColors = [];
        let colorsChanged = false;
        $scope.inview = !!$scope.isAiAssistant;
        $scope.edited = false;
        $scope.setWatcher = true;

        function updateMetas() {
            $scope.meta = { ...AdvancedGraphWidgetConfigs['CustomWidget'] };
            $scope.meta.fields = {};
            $scope.meta.extraOptions.yAxis = [];

            const options = generateConfigBasedOfSeries($scope.meta, $scope.series);
            $scope.meta = options.meta;
            $scope.graphYColors = options.yColorsFromSeries;
            const totalAxes = Object.keys(options.yColorsFromSeries).length;
            $scope.meta.extraOptions.options = {
                chart: {
                    marginRight: RIGHT_MARGIN_AXES[totalAxes],
                    marginLeft: LEFT_MARGIN_AXES[totalAxes]
                }
            }
        }

        // should be called only after graph is updated and metas are updated, due to color series bug
        function updateColors() {
            const colors = WhitelabelService.getGraphColors()
            Object.keys($scope.graphYColors).forEach(function (idx) {
                $scope.chart.yAxis[idx] = {
                    ...$scope.chart.yAxis[idx], labels: {
                        ...$scope.chart.yAxis[idx].labels,
                        style: {
                            color: colors[$scope.graphYColors[idx]],
                            textOverflow: 'none'
                        },
                    },
                    title: {
                        text: undefined
                    }
                }
            })
        }

        $scope.$watch('loading', function (loading) {
            if ($scope.chart) {
                $scope.chart.loading = loading;
            }
        });

        $scope.$watch('size', () => {
            if (!$scope.chart || !$scope.chart.getHighcharts) return;
            $scope.chart.getHighcharts().reflow();
        });

        $scope.formatSeriesTitle = function (meta) {
            let result = '';
            if (meta.description) {
                result += ` ${meta.description}`;
            }
            if (meta.formatWithUnit) {
                const unit = meta.formatWithUnit
                    .replace(meta.formatValueOnly, '')
                    .replace('%%', '%')
                    .replace(/%\.\dF/, '')
                    .trim();
                if (unit) {
                    result += `<span>(${unit})</span>`;
                }
            }
            return result;
        };

        $scope.seriesLabelStyle = function ($index) {
            const colors = WhitelabelService.getGraphColors();
            if ($scope.series && $scope.series[$index] && $scope.series[$index].serieColor) {
                const labelStyle = {
                    color: colors[$scope.series[$index].serieColor],
                };
                if (!$scope.chart.series[$index].visible) {
                    labelStyle["opacity"] = 0.5;
                }
                return labelStyle;
            } else {
                return { 'color': colors['blue'] };
            }
        };

        $scope.widgetTitle = function () {
            let title = $scope.title;
            if ($scope.customName) {
                title = `${$scope.customName} - ` + title;
            }
            return title;
        };

        $scope.popup = {
            elem: null,
            active: false,
            widgetVisible: true,
            useMinMax: null,
            clicked: false,
        };

        $scope.deleteCustomWidget = () => {
            $scope.$emit('deleteCustomWidget', { idCustomWidget: $scope.widgetId, name: $scope.title });
            $scope.onOutsidePopupClick();
        }

        $scope.onOutsidePopupClick = () => {
            if ($scope.popup.active) {
                $scope.popup.active = false;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
                setTimeout(() => { $scope.popup.clicked = false; $scope.$applyAsync(); }, 1);
                return true;
            } else {
                return false;
            }
        };

        $scope.clickSettings = function () {
            if (!$scope.popup.clicked && !$scope.popup.active) {
                $scope.popup.active = true;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
            }
        };

        $scope.$watch('popup.widgetVisible', (isVisible) => {
            if ($scope.itemState) $scope.itemState.active = isVisible;
        });

        $scope.clickExpand = () => {
            if ($scope.itemState) $scope.itemState.expanded = !$scope.itemState.expanded;
        }

        /**
         * Remove the null points from the data points of graphs:
         * This is done, because null points break the line of a graph
         */
        function filterNullPoints(data) {
            const cp = { ...data }
            Object.keys(data).forEach(function (key) {
                const dataAttributeId = parseInt(key.split("_")[0]);
                if (FILTER_NULL_POINTS_ATTRIBUTE_IDS.includes(dataAttributeId)) {
                    cp[key] = data[key].filter((v) => v[1] !== null);
                }
            });
            return cp;
        }

        $q.all([GraphService.ready(), locale.ready('advanced')]).then(function () {
            $scope.load = function load() {
                if (($scope.inview && !loaded && $scope.data) || ($scope.data && !loaded && $scope.edited)) {
                    loaded = true;
                    updateMetas();
                    $scope.current = $scope.meta;

                    // if it's not the advanced page then we want to regenerate the chart
                    // since the displayed data and the series colours can change
                    if (
                        !$scope.isAdvancedView ||
                        !$scope.chart ||
                        colorsChanged ||
                        ($scope.isAiAssistant && $scope.edited)
                    ) {
                        colorsChanged = false;
                        $scope.chart = GraphService.getAdvancedConfig(
                            $scope.current,
                            getGraphColors(),
                            lineColors
                        );
                        $scope.popup.useMinMax = handleInitialLocalStorageBool(localStorageService.get(storageKey()), "use_range");
                        initial_visibility();
                    }


                    const parsedMetas = parseMetas($scope.data.meta);

                    $scope.metas = Object.values(parsedMetas).filter(function (field) {
                        return $scope.current.fields.hasOwnProperty(field.code);
                    });

                    $scope.metas = sortBy($scope.metas, function (meta) {
                        return Object.keys($scope.current.fields).indexOf(
                            meta.code
                        );
                    });

                    const today = new Date();
                    const m = moment;
                    let range = {
                        startDate: m(today).startOf("day").valueOf() / 1000,
                        endDate: today.getTime() / 1000,
                        endsNow: true,
                        interval: "hours",
                        precision: 3600,
                        reloadEndDate: m(today).startOf("hour") / 1000,
                        reloadInterval: "15mins",
                        reloadPrecision: 900,
                        reloadStartDate:
                            m(today).startOf("day").valueOf() / 1000,
                    };

                    if ($scope.range) {
                        range = {
                            ...range,
                            ...$scope.range
                        }
                    }

                    GraphService.updateConfig(
                        $scope.chart,
                        filterNullPoints($scope.data.data),
                        parsedMetas,
                        $scope.current.fields,
                        false,
                        false,
                        range,
                        null,
                        $scope.popup.useMinMax,
                        $scope.data.yAxisProperties || $scope.yAxisProperties
                    );

                    updateColors();
                    $scope.edited = false;
                    if ($scope.isAdvancedView) {
                        mixinZoomState($scope, $scope.setWatcher);
                        $scope.setWatcher = false;
                    }
                }
            }


            $scope.refresh = function refresh() {
                loaded = false;
                const newLineColors = $scope.series
                    ? $scope.series.map(({ serieColor }) => serieColor)
                    : [];
                if (!_.isEqual(lineColors, newLineColors)) {
                    lineColors = newLineColors;
                    colorsChanged = true;
                }
                updateStorageRanges();
                if ($scope.isAiAssistant) {
                    $scope.edited = true;
                }
                $scope.load();
            };

            $scope.$watch('[data, popup.useMinMax, series]', () => $scope.refresh(), true);
            $scope.$watchGroup(['inview'], () => $scope.load());
        });

        function parseMetas(meta) {
            const clonedMetas = cloneDeep(meta);
            const countDescriptions = _.countBy(clonedMetas, ({ description }) => description);
            const metasWithInstance = Object.entries(clonedMetas).map(([codeKey, options]) => {
                // codeKey has the form: `${dataAttributeId}_${instance}`
                const formatCode = codeKey.split("_");
                const instance = formatCode[formatCode.length - 1];
                const dataAttribute = formatCode[0];
                const code = options.code.split("_")[0];

                if (MONETARY_ATTRIBUTES_CODES.find(c => c === code)) {
                    options.formatWithUnit = options.formatWithUnit.replace('mu', $scope.installation.currency.sign);
                }

                const series = $scope.series.find(series => {
                    return +series.instance === +instance && +series.idDataAttribute === +dataAttribute;
                });
                if (series === undefined) return;

                options.class = series.class;

                const multiple = countDescriptions[options.description] > 1;
                options.description = `<span>- ${options.description}</span>`;
                if (multiple) options.description = `<span>[${instance}]</span>` + options.description;
                options.description = `<span>${series.deviceName}</span>` + options.description;

                return [codeKey, options]
            })
            return Object.fromEntries(_.filter(metasWithInstance, val => val !== undefined));
        }

        function updateStorageLabels() {
            const visibilities = $scope.chart.series.map(x => x.visible);
            if ($scope.current.allowSum && !$scope.current.totalOnly) {
                visibilities[visibilities.length - 1] = !visibilities[visibilities.length - 1]
            }
            const all_visibile = visibilities.every(x => x);
            if (all_visibile) {
                const old_value = localStorageService.get(storageKey());
                if (old_value && old_value.labels) {
                    delete old_value.labels;
                    if (Object.keys(old_value).length) {
                        localStorageService.set(storageKey(), old_value);
                    } else {
                        localStorageService.remove(storageKey());
                    }
                }
            } else {
                const old_value = localStorageService.get(storageKey()) || {}
                if ($scope.current.allowSum && !$scope.current.totalOnly) {
                    visibilities[visibilities.length - 1] = !visibilities[visibilities.length - 1]
                }
                old_value.labels = visibilities;
                localStorageService.set(storageKey(), old_value)
            }
        }

        function updateStorageRanges() {
            let old_value = localStorageService.get(storageKey())

            if ($scope.popup.useMinMax === null) { // value === null
                // no value in parameter is updated with the local storage value
                if (old_value && old_value["use_range"] !== null) {
                    $scope.popup.useMinMax = old_value["use_range"] // initial value
                }
            }
            else {
                const result = handleLocalStorageBool( old_value, $scope.popup.useMinMax, "use_range")
                if(result){
                    localStorageService[result.action](storageKey(), result.value);
                }
            }
        }

        function storageKey() {
            return ['custom_widget_settings', $scope.installation.id, $scope.widgetId].join('_');
        }

        $scope.toggle = function ($index) {
            const series = $scope.chart.series[$index];
            series.visible = !series.visible;
            updateStorageLabels()

            const parsedMetas = parseMetas($scope.data.meta);
            GraphService.setYAxisConfigsForGroups($scope.chart, parsedMetas, $scope.data.yAxisProperties || $scope.yAxisProperties)
        };

        function getGraphColors() {
            return $scope.current.getColors(WhitelabelService.getGraphColors());
        }

        function initial_visibility() {
            const local_settings = localStorageService.get(storageKey());
            if (local_settings && local_settings.labels) {
                const active_subtitles = local_settings.labels
                if (active_subtitles && active_subtitles.length === $scope.chart.series.length) {
                    for (let i = 0; i < active_subtitles.length; i++) {
                        $scope.chart.series[i].visible = active_subtitles[i]
                    }
                }
            }
        }
    }
}
